import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class KeycloakAuthService {
  private readonly tokenStorageKey = 'configurator_jwt';
  private readonly idTokenStorageKey = 'configurator_id_token';
  private readonly refreshTokenStorageKey = 'configurator_refresh_token';
  private readonly verifierStorageKey = 'pkce_verifier';
  private readonly stateStorageKey = 'oidc_state';
  private refreshInFlight: Promise<boolean> | null = null;

  async handleRedirectCallback(): Promise<void> {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    if (!code || !state) {
      return;
    }

    const expectedState = sessionStorage.getItem(this.stateStorageKey);
    const verifier = sessionStorage.getItem(this.verifierStorageKey);
    if (!expectedState || !verifier || expectedState !== state) {
      this.clearOidcSession();
      this.clearAuth();
      this.cleanUrl();
      throw new Error('Invalid OIDC state');
    }

    const tokenEndpoint = this.tokenEndpoint();
    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: environment.keycloakClientId,
      code,
      redirect_uri: this.redirectUri(),
      code_verifier: verifier
    });

    const response = await fetch(tokenEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString()
    });

    if (!response.ok) {
      this.clearOidcSession();
      this.clearAuth();
      this.cleanUrl();
      throw new Error('Token exchange failed');
    }

    const tokenResponse = await response.json() as { access_token?: string; id_token?: string; refresh_token?: string };
    if (!tokenResponse.access_token) {
      this.clearOidcSession();
      this.clearAuth();
      this.cleanUrl();
      throw new Error('No access_token in response');
    }

    this.storeTokens(tokenResponse);
    this.clearOidcSession();
    this.cleanUrl();
  }

  isRedirectCallback(): boolean {
    const params = new URLSearchParams(window.location.search);
    return params.has('code') && params.has('state');
  }

  async login(): Promise<void> {
    const verifier = this.randomString(64);
    const challenge = await this.sha256Base64Url(verifier);
    const state = this.randomString(32);

    sessionStorage.setItem(this.verifierStorageKey, verifier);
    sessionStorage.setItem(this.stateStorageKey, state);

    const authEndpoint = `${environment.keycloakBaseUrl}/realms/${environment.keycloakRealm}/protocol/openid-connect/auth`;
    const url = new URL(authEndpoint);
    url.searchParams.set('client_id', environment.keycloakClientId);
    url.searchParams.set('response_type', 'code');
    url.searchParams.set('scope', 'openid profile email');
    url.searchParams.set('redirect_uri', this.redirectUri());
    url.searchParams.set('state', state);
    url.searchParams.set('code_challenge_method', 'S256');
    url.searchParams.set('code_challenge', challenge);

    window.location.assign(url.toString());
  }

  logout(): void {
    const logoutEndpoint = `${environment.keycloakBaseUrl}/realms/${environment.keycloakRealm}/protocol/openid-connect/logout`;
    const url = new URL(logoutEndpoint);
    const idToken = this.getIdToken();
    url.searchParams.set('client_id', environment.keycloakClientId);
    if (idToken) {
      url.searchParams.set('id_token_hint', idToken);
    }
    url.searchParams.set('post_logout_redirect_uri', this.redirectUri());
    this.clearAuth();
    this.clearOidcSession();
    window.location.assign(url.toString());
  }

  async ensureSession(): Promise<boolean> {
    const token = this.getToken();
    if (token && !this.isTokenExpired(token, 30)) {
      return true;
    }
    return this.refreshTokens();
  }

  async getValidAccessToken(): Promise<string | null> {
    const token = this.getToken();
    if (token && !this.isTokenExpired(token, 30)) {
      return token;
    }
    const refreshed = await this.refreshTokens();
    return refreshed ? this.getToken() : null;
  }

  async refreshSession(): Promise<boolean> {
    return this.refreshTokens();
  }

  getStoredAccessToken(): string | null {
    return this.getToken();
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) {
      return false;
    }
    return !this.isTokenExpired(token);
  }

  tenantId(): string {
    const token = this.getToken();
    const payload = token ? this.decodeJwtPayload(token) : null;
    const tenantId = payload?.['tenant_id'];
    return typeof tenantId === 'string' ? tenantId : '';
  }

  private getToken(): string | null {
    return localStorage.getItem(this.tokenStorageKey);
  }

  private getIdToken(): string | null {
    return localStorage.getItem(this.idTokenStorageKey);
  }

  private getRefreshToken(): string | null {
    return localStorage.getItem(this.refreshTokenStorageKey);
  }

  private tokenEndpoint(): string {
    return `${environment.keycloakBaseUrl}/realms/${environment.keycloakRealm}/protocol/openid-connect/token`;
  }

  private storeTokens(tokenResponse: { access_token?: string; id_token?: string; refresh_token?: string }): void {
    if (!tokenResponse.access_token) {
      throw new Error('No access token available');
    }
    localStorage.setItem(this.tokenStorageKey, tokenResponse.access_token);
    if (tokenResponse.id_token) {
      localStorage.setItem(this.idTokenStorageKey, tokenResponse.id_token);
    } else {
      localStorage.removeItem(this.idTokenStorageKey);
    }
    if (tokenResponse.refresh_token) {
      localStorage.setItem(this.refreshTokenStorageKey, tokenResponse.refresh_token);
    }
  }

  private async refreshTokens(): Promise<boolean> {
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }

    this.refreshInFlight = this.performRefresh().finally(() => {
      this.refreshInFlight = null;
    });
    return this.refreshInFlight;
  }

  private async performRefresh(): Promise<boolean> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      this.clearAuth();
      return false;
    }

    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: environment.keycloakClientId,
      refresh_token: refreshToken
    });

    const response = await fetch(this.tokenEndpoint(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString()
    });

    if (!response.ok) {
      this.clearAuth();
      return false;
    }

    const tokenResponse = await response.json() as { access_token?: string; id_token?: string; refresh_token?: string };
    if (!tokenResponse.access_token) {
      this.clearAuth();
      return false;
    }

    this.storeTokens(tokenResponse);
    return true;
  }

  private isTokenExpired(token: string, skewSeconds = 0): boolean {
    const payload = this.decodeJwtPayload(token);
    const exp = payload?.['exp'];
    if (typeof exp !== 'number') {
      return true;
    }
    return exp * 1000 <= Date.now() + skewSeconds * 1000;
  }

  private clearAuth(): void {
    localStorage.removeItem(this.tokenStorageKey);
    localStorage.removeItem(this.idTokenStorageKey);
    localStorage.removeItem(this.refreshTokenStorageKey);
  }

  private clearOidcSession(): void {
    sessionStorage.removeItem(this.verifierStorageKey);
    sessionStorage.removeItem(this.stateStorageKey);
  }

  private redirectUri(): string {
    return `${window.location.origin}/`;
  }

  private cleanUrl(): void {
    const clean = `${window.location.origin}${window.location.pathname}`;
    window.history.replaceState({}, document.title, clean);
  }

  private randomString(length: number): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    const bytes = new Uint8Array(length);
    crypto.getRandomValues(bytes);
    return Array.from(bytes).map((b) => chars[b % chars.length]).join('');
  }

  private async sha256Base64Url(input: string): Promise<string> {
    const data = new TextEncoder().encode(input);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return this.base64UrlEncode(new Uint8Array(digest));
  }

  private base64UrlEncode(bytes: Uint8Array): string {
    let binary = '';
    bytes.forEach((b) => {
      binary += String.fromCharCode(b);
    });
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
  }

  private decodeJwtPayload(token: string): Record<string, unknown> | null {
    const parts = token.split('.');
    if (parts.length < 2) {
      return null;
    }
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = payload.length % 4 === 0 ? '' : '='.repeat(4 - (payload.length % 4));
    try {
      return JSON.parse(atob(payload + pad)) as Record<string, unknown>;
    } catch {
      return null;
    }
  }
}
